import { useEffect, useState, useMemo, useRef } from "react";
import moment from "moment";
import { MediaPlayer } from "./MediaPlayer";
import { useSelector } from "react-redux";
import AnimatedWrap from "../../../../SharedComponents/DragDrop/animateMedia";
import { useTranslation } from "react-i18next";

function ScreenMediaRenderer({ area, elapsedSeconds }) {
    const mediaList = area?.campaign_media?.MAIN || [];
    const animCfg = area?.campaign_media?.media_animation || {};
    const durations = useMemo(
        () => mediaList.map(m => Number(m.custom_duration || m.default_duration || 10)),
        [mediaList]
    );

    const screenDuration = useMemo(
        () => durations.reduce((sum, d) => sum + d, 0),
        [durations]
    );

    const starts = useMemo(() => {
        const arr = [];
        let acc = 0;
        for (const d of durations) {
            arr.push(acc);
            acc += d;
        }
        return arr;
    }, [durations]);

    // Track only when the item changes; freeze offset for MediaPlayer
    const [currentIndex, setCurrentIndex] = useState(0);
    const [frozenOffset, setFrozenOffset] = useState(0);
    const prevIndexRef = useRef(-1);

    useEffect(() => {
        if (!screenDuration || mediaList.length === 0) return;

        // normalize elapsed into current loop
        const loopTime = ((elapsedSeconds % screenDuration) + screenDuration) % screenDuration;

        // find active index
        let idx = 0;
        for (let i = 0; i < starts.length; i++) {
            const start = starts[i];
            const end = start + durations[i];
            if (loopTime >= start && loopTime < end) {
                idx = i;
                break;
            }
        }

        // only update when item changes
        if (idx !== prevIndexRef.current) {
            prevIndexRef.current = idx;
            setCurrentIndex(idx);
            setFrozenOffset(loopTime - starts[idx]);
        }
        // else: keep frozenOffset stable to avoid per-second prop churn
    }, [elapsedSeconds, screenDuration, mediaList.length, starts, durations]);

    const active = mediaList[currentIndex] || null;
    if (!active || !screenDuration) return null;

    // Animation config
    const enabled = !!animCfg.enabled;
    const type = animCfg.type || "FADE";
    const direction = animCfg.direction || "CENTER";
    const durationMs = (animCfg.duration ? animCfg.duration * 1000 : 600) || 600;

    const loopCount = Math.floor(elapsedSeconds / screenDuration);
    const mediaId =
        active.assigned_id || active.media_id || active.layout_id || active.playlist_id || "m";
    const itemKey = `${mediaId}-${loopCount}`;

    const node = (
        <MediaPlayer
            key={itemKey}                                  // remount only on switch/loop
            data={active}
            elapsedSeconds={frozenOffset}                  // stable between switches
            customDuration={Number(active.custom_duration || active.default_duration || 10)}
        />
    );

    return (
        <div
            className="absolute"
            style={{
                left: `${area.start_margin}%`,
                top: `${area.top_margin}%`,
                width: `${area.width}%`,
                height: `${area.height}%`,
                zIndex: 1,
            }}
        >
            {enabled ? (
                <AnimatedWrap
                    itemKey={itemKey}
                    type={type}
                    direction={direction}
                    durationMs={durationMs}
                    className="absolute inset-0"
                >
                    {node}
                </AnimatedWrap>
            ) : (
                node
            )}
        </div>
    );
}

function LayoutPreview({ layout, pause_start_time, startTime }) {
    const { t } = useTranslation();
    const { paused } = useSelector((state) => state.device);
    const [tick, setTick] = useState(0);

    // Only tick when not paused
    useEffect(() => {
        if (paused) return;
        const id = setInterval(() => setTick(t => t + 1), 1000);
        return () => clearInterval(id);
    }, [paused]);

    // Compute elapsed once here and pass down
    const now = paused && pause_start_time ? moment(pause_start_time) : moment();
    const start = moment(startTime);
    const elapsedSeconds = now.diff(start, "seconds");

    const screens_setting = layout?.layout || [];
    const isAnimatedLayout = layout?.is_animated_layout;

    return (
        <div className="place-items-center w-full text-center h-full">
            {isAnimatedLayout ? (
                <div className="screen w-100 d-flex h-100 bg-secondary text-white text-muted justify-content-center align-items-end pb-5">
                    <span>{t("devices.preview_animated_layout_unsupported")}</span>
                </div>
            ) : (
                <div className="flex relative justify-center w-full h-full">
                    <div className="relative w-full h-full rounded-md bg-black-100">
                        {screens_setting.map((area, index) => (
                            <ScreenMediaRenderer
                                key={index}
                                area={area}
                                elapsedSeconds={elapsedSeconds}    // single time source
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

export default LayoutPreview;
